import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Navbar,
  Collapse,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  Menu,
  MenuHandler,
  MenuList,
  MenuItem,
} from "./mt-lite";
import {
  ChevronDownIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import {
  GlobeAmericasIcon,
  NewspaperIcon,
  RectangleGroupIcon,
  SquaresPlusIcon,
  SunIcon,
  TagIcon,
  UserGroupIcon,
  TruckIcon,
  RocketLaunchIcon,
  BuildingLibraryIcon,
  UserIcon,
  PaintBrushIcon,
  MapIcon,
  ArrowTrendingUpIcon,
  BuildingOfficeIcon,
  FingerPrintIcon,
  TrophyIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  DocumentTextIcon
} from "@heroicons/react/24/solid";
import { ShieldCheckIcon } from "@heroicons/react/24/outline";
import "./navbarOverride.css"; // override dosyasını ekleyin
import { SunIcon as MoonIcon } from "@heroicons/react/24/solid";
import { getColor, setTheme, getCurrentTheme } from "../../theme/colors";

const serdivanMenuItems = [
  {
    title: "Serdivan Tarihi",
    description: "Geçmişten Günümüze Serdivan",
    icon: FingerPrintIcon,
    path: "/serdivan-tarihi"
  },
  {
    title: "Serdivan'ın Coğrafi Alanı",
    description: "İlçemizin Coğrafyası",
    icon: MapIcon,
    path: "/serdivan-cografya"
  },
  {
    title: "Serdivan'ın Kentsel Gelişimi",
    description: "İlçemizin Kentsel Bilgileri",
    icon:   ArrowTrendingUpIcon,
    path: "/serdivan-gelisim"
  },
  {
    title: "Muhtarlıklar",
    description: "Serdivan'a Bağlı Muhtarlıklar",
    icon: BuildingOfficeIcon,
    path: "/muhtarliklar"
  },
  {
    title: "Kardeş Şehirler",
    description: "Serdivan'ın Kardeş Şehirleri",
    icon: GlobeAmericasIcon,
    path: "/kardes-sehirler"
  },
];

const kurumsalMenuItems = [
  {
    title: "Belediye Başkanı",
    description: "Belediye başkanımız hakkında bilgi",
    icon: UserIcon,
    path: "/belediye-baskani"
  },
  {
    title: "Başkan Yardımcıları",
    description: "Yardımcıların görevleri",
    icon: UserGroupIcon,
    path: "/baskan-yardimcilari"
  },
  {
    title: "Müdürlükler",
    description: "Tüm müdürlüklerin listesi",
    icon: RectangleGroupIcon,
    path: "/mudurlukler"
  },
  {
    title: "Belediye Meclisi",
    description: "Meclis bilgileri",
    icon: NewspaperIcon,
    submenu: [
      { title: "Meclis Üyeleri", path: "/meclis-uyeleri" },
      { title: "Meclis Gündemleri", path: "/meclis-gundemleri" },
      { title: "Meclis Kararları", path: "/meclis-kararlari" },
      { title: "Komisyon Raporları", path: "/komisyon-raporlari" },
      { title: "Meclis Görev ve Yetkileri", path: "/meclisgorevleri" },
    ],
  },
  {
    title: "Belediye Encümeni",
    description: "Encümen bilgileri",
    icon: TagIcon,
    submenu: [
      { title: "Encümen Üyeleri", path: "/belediye-encumeni" },
      { title: "Encümen Görev ve Yetkileri", path: "/encumen-gorevleri" },
    ],
  },
  {
    title: "Yönetim",
    description: "Yönetim birimleri",
    icon: BuildingLibraryIcon,
    submenu: [
      { title: "Vizyon ve Misyon", path: "/vizyon-ve-misyon" },
      { title: "Stratejik Planlar", path: "/stratejik-planlar" },
      { title: "Performans Programları", path: "/performans-programlari" },
      { title: "Faaliyet Raporları", path: "/faaliyet-raporlari" },
      { title: "Organizasyon Şeması", path: "/organizasyon-semasi" },
      { title: "Mali Beklenti Raporları", path: "/mali-beklenti" },
    ],
  },
  {
    title: "Etik",
    description: "Etik kurallar",
    icon: ShieldCheckIcon,
    path: "/etik"
  },
  {
    title: "Belediye Kuralları Yönetmeliği",
    description: "Serdivan Belediyesi Belediye Kuralları Yönetmeliği",
    icon: DocumentTextIcon,
    external: true,
    path: "/uploads/Serdivan_Belediyesi_Belediye_Kurallari_Yoenetmeligi_a095e2ef02.pdf"
  },
];

// DESKTOP MENU COMPONENT
function DesktopMenu({ goToFirstSlide }) {
  const [isSerdivanOpen, setIsSerdivanOpen] = React.useState(false);
  const [isKurumsalOpen, setIsKurumsalOpen] = React.useState(false);

  // Scroll lock functionality
  React.useEffect(() => {
    if (isSerdivanOpen || isKurumsalOpen) {
      // Disable scroll
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      // Enable scroll
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isSerdivanOpen, isKurumsalOpen]);

  // Close menus on escape key
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsSerdivanOpen(false);
        setIsKurumsalOpen(false);
      }
    };

    if (isSerdivanOpen || isKurumsalOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isSerdivanOpen, isKurumsalOpen]);

  return (
    <List
      className="mt-0 mb-0 flex-row flex-nowrap p-1 items-center justify-start gap-1"
      style={{
        background: getColor("navbar"),
        color: getColor("navbarText"),
      }}
    >
      <Link
        to="/"
        onClick={e => {
          if (location.pathname === "/" && typeof goToFirstSlide === "function") {
            e.preventDefault();
            goToFirstSlide();
          }
        }}
      >
        <ListItem
          className="flex items-center gap-2 py-2 px-3 font-bold min-w-fit whitespace-nowrap"
          style={{
            color: getColor("navbarText"),
          }}
        >
          Ana Sayfa
        </ListItem>
      </Link>

      {/* Serdivan Menu */}
      <Menu open={isSerdivanOpen} handler={setIsSerdivanOpen} allowHover={true}>
        <MenuHandler>
          <ListItem
            className="flex items-center gap-2 py-2 px-3 font-bold min-w-fit"
            style={{
              color: getColor("navbarText"),
            }}
          >
            Serdivan
            <ChevronDownIcon
              strokeWidth={2.5}
              className={`h-3 w-3 transition-transform ${isSerdivanOpen ? "rotate-180" : ""}`}
              style={{ color: getColor("navbarText") }}
            />
          </ListItem>
        </MenuHandler>
        <MenuList
          className="max-w-screen-xl material-tailwind-menu-list"
          style={{
            background: getColor("surface"),
            color: getColor("text"),
            zIndex: 9999,
            position: "absolute",
            top: "100%",    // navbar'ın hemen altından açılır
            left: 0,
            width: "100%",
            maxWidth: "100%",
            overflow: "visible",
            padding: "1.5rem 1.75rem",
          }}
        >
          <div className="grid grid-cols-3 gap-x-8 gap-y-3">
            {serdivanMenuItems.map((item, key) => (
              <Link to={item.path} key={key}>
                <MenuItem
                  className="flex items-center gap-3 rounded-lg p-2"
                  style={{
                    background: getColor("surface"),
                    color: getColor("text"),
                  }}
                >
                  <div
                    className="flex items-center justify-center rounded-lg p-2"
                    style={{ background: getColor("card") }}
                  >
                    {React.createElement(item.icon, {
                      className: "h-6 w-6",
                      style: { color: getColor("primary") },
                    })}
                  </div>
                  <div>
                    <Typography
                      variant="h6"
                      className="text-sm font-bold"
                      style={{ color: getColor("text") }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="paragraph"
                      className="text-xs"
                      style={{ color: getColor("textSecondary") }}
                    >
                      {item.description}
                    </Typography>
                  </div>
                </MenuItem>
              </Link>
            ))}
          </div>
        </MenuList>
      </Menu>

      {/* Kurumsal Menu */}
      <Menu open={isKurumsalOpen} handler={setIsKurumsalOpen} allowHover={true}>
        <MenuHandler>
          <ListItem className="flex items-center gap-2 py-2 px-3 font-bold min-w-fit"
            style={{
              color: getColor("navbarText"),
            }}
          >
            Kurumsal
            <ChevronDownIcon
              strokeWidth={2.5}
              className={`h-3 w-3 transition-transform ${isKurumsalOpen ? "rotate-180" : ""}`}
              style={{ color: getColor("navbarText") }}
            />
          </ListItem>
        </MenuHandler>
        <MenuList
          className="max-w-screen-xl material-tailwind-menu-list"
          style={{
            background: getColor("surface"),
            color: getColor("text"),
            zIndex: 9999,
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            maxWidth: "100%",
            overflow: "visible",
            padding: "1.5rem 1.75rem",
          }}
        >
          <div className="grid grid-cols-3 gap-x-8 gap-y-3">
            {kurumsalMenuItems.map((item, key) => (
              <div key={key}>
                {item.submenu ? (
                  <Menu placement="right-start" allowHover offset={8}>
                    <MenuHandler>
                      <MenuItem className="flex items-center gap-3 rounded-lg w-max pr-8 border border-transparent hover:border-gray-100"
                        style={{
                          background: getColor("surface"),
                          color: getColor("text"),
                        }}
                      >
                        <div
                          className="flex items-center justify-center rounded-lg p-2"
                          style={{ background: getColor("card") }}
                        >
                          {React.createElement(item.icon, {
                            className: "h-6 w-6",
                            style: { color: getColor("primary") },
                          })}
                        </div>
                        <div className="flex items-center gap-2">
                          <div>
                            <Typography variant="h6" className="text-sm font-bold"
                              style={{ color: getColor("text") }}
                            >
                              {item.title}
                            </Typography>
                            <Typography variant="paragraph" className="text-xs"
                              style={{ color: getColor("textSecondary") }}
                            >
                              {item.description}
                            </Typography>
                          </div>
                          <ChevronRightIcon className="w-4 h-4"
                            style={{ color: getColor("textSecondary"), marginLeft: "0.5rem" }}
                          />
                        </div>
                      </MenuItem>
                    </MenuHandler>
                    <MenuList
                      className="material-tailwind-menu-list p-2"
                      style={{
                        background: getColor("surface"),
                        color: getColor("text"),
                        zIndex: 10000,
                        minWidth: "230px",
                        maxHeight: "calc(100vh - 64px)",
                        overflowY: "auto",
                        border: `1px solid ${getColor("border")}`,
                        // Güçlü yüzen gölge: alt menü, panelin üstünde net bir kart gibi dursun
                        // (zayıf gölgeyle içeriğe karışıp "berbat" görünüyordu).
                        boxShadow: "0 12px 40px rgba(0,0,0,0.18), 0 4px 12px rgba(0,0,0,0.10)",
                        borderRadius: "12px"
                      }}
                    >
                      {item.submenu.map((subItem, subKey) => (
                        <Link to={subItem.path} key={subKey}>
                          <MenuItem className="flex items-center gap-3 rounded-lg p-2"
                            style={{
                              background: getColor("card"),
                              color: getColor("cardText"),
                            }}
                          >
                            <div>
                              <Typography variant="h6" className="text-sm font-bold"
                                style={{ color: getColor("text") }}
                              >
                                {subItem.title}
                              </Typography>
                            </div>
                          </MenuItem>
                        </Link>
                      ))}
                    </MenuList>
                  </Menu>
                ) : item.external ? (
                  <a href={item.path} target="_blank" rel="noopener noreferrer">
                    <MenuItem className="flex items-center gap-3 rounded-lg p-2"
                      style={{
                        background: getColor("surface"),
                        color: getColor("text"),
                      }}
                    >
                      <div
                        className="flex items-center justify-center rounded-lg p-2"
                        style={{ background: getColor("card") }}
                      >
                        {React.createElement(item.icon, {
                          className: "h-6 w-6",
                          style: { color: getColor("primary") },
                        })}
                      </div>
                      <div>
                        <Typography variant="h6" className="text-sm font-bold"
                          style={{ color: getColor("text") }}
                        >
                          {item.title}
                        </Typography>
                        <Typography variant="paragraph" className="text-xs"
                          style={{ color: getColor("textSecondary") }}
                        >
                          {item.description}
                        </Typography>
                      </div>
                    </MenuItem>
                  </a>
                ) : (
                  <Link to={item.path}>
                    <MenuItem className="flex items-center gap-3 rounded-lg p-2"
                      style={{
                        background: getColor("surface"),
                        color: getColor("text"),
                      }}
                    >
                      <div
                        className="flex items-center justify-center rounded-lg p-2"
                        style={{ background: getColor("card") }}
                      >
                        {React.createElement(item.icon, {
                          className: "h-6 w-6",
                          style: { color: getColor("primary") },
                        })}
                      </div>
                      <div>
                        <Typography variant="h6" className="text-sm font-bold"
                          style={{ color: getColor("text") }}
                        >
                          {item.title}
                        </Typography>
                        <Typography variant="paragraph" className="text-xs"
                          style={{ color: getColor("textSecondary") }}
                        >
                          {item.description}
                        </Typography>
                      </div>
                    </MenuItem>
                  </Link>
                )}
              </div>
            ))}
          </div>
        </MenuList>
      </Menu>

      <Link to="/hizmetlerimiz">
        <ListItem
          className="flex items-center gap-2 py-2 px-3 font-bold min-w-fit"
          style={{ color: getColor("navbarText") }}
        >
          Hizmetlerimiz
        </ListItem>
      </Link>
      <Link to="/duyurular">
        <ListItem
          className="flex items-center gap-2 py-2 px-3 font-bold min-w-fit"
          style={{ color: getColor("navbarText") }}
        >
          Duyurular
        </ListItem>
      </Link>
      <Link to="/haberler">
        <ListItem
          className="flex items-center gap-2 py-2 px-3 font-bold min-w-fit"
          style={{ color: getColor("navbarText") }}
        >
          Haberler
        </ListItem>
      </Link>
      <Link to="/etkinlik-takvimi">
        <ListItem
          className="flex items-center gap-2 py-2 px-3 font-bold min-w-fit"
          style={{ color: getColor("navbarText") }}
        >
          Etkinlikler
        </ListItem>
      </Link>
    </List>
  );
}

// MOBILE MENU COMPONENT
function MobileMenu({ goToFirstSlide, location, onNavigate }) {
  // Varsayılan olarak hepsi kapalı
  const [openGroup, setOpenGroup] = React.useState(""); // "serdivan" | "kurumsal" | ""

  // Alt menüler için state
  const [isMeclisOpen, setIsMeclisOpen] = React.useState(false);
  const [isEncumenOpen, setIsEncumenOpen] = React.useState(false);
  const [isYonetimOpen, setIsYonetimOpen] = React.useState(false);

  // Grup değişince alt menüleri kapat
  React.useEffect(() => {
    setIsMeclisOpen(false);
    setIsEncumenOpen(false);
    setIsYonetimOpen(false);
  }, [openGroup]);

  // Serdivan veya Kurumsal açıldığında diğerini kapat
  const handleGroupToggle = (group) => {
    setOpenGroup(prev => prev === group ? "" : group);
  };

  // Alt menüleri toggle et
  const handleSubmenuToggle = (title) => {
    if (title === "Belediye Meclisi") setIsMeclisOpen(open => !open);
    if (title === "Belediye Encümeni") setIsEncumenOpen(open => !open);
    if (title === "Yönetim") setIsYonetimOpen(open => !open);
  };

  // ESC tuşuna basınca menüleri kapat
  React.useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") setOpenGroup("");
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  return (
    <List className="p-0" style={{ background: getColor("navbar") }}>
      <Link
        to="/"
        onClick={e => {
          if (location.pathname === "/" && typeof goToFirstSlide === "function") {
            e.preventDefault();
            goToFirstSlide();
          }
          if (onNavigate) onNavigate();
        }}
      >
        <ListItem
          className="py-2 px-4 font-bold min-w-fit"
          style={{ color: getColor("navbarText"), background: getColor("navbar") }}
        >
          Ana Sayfa
        </ListItem>
      </Link>

      {/* Serdivan Group */}
      <ListItem
        onClick={() => handleGroupToggle("serdivan")}
        className="flex justify-between items-center py-2 px-4 font-bold cursor-pointer min-w-fit"
        aria-expanded={openGroup === "serdivan"}
        tabIndex={0}
        style={{
          color: getColor("navbarText"),
          background: getColor("navbar"),
        }}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") handleGroupToggle("serdivan");
        }}
      >
        Serdivan
        <ChevronDownIcon
          strokeWidth={2.5}
          className={`h-4 w-4 transition-transform ${openGroup === "serdivan" ? "rotate-180" : ""}`}
          style={{ color: getColor("navbarText") }}
        />
      </ListItem>
      <Collapse open={openGroup === "serdivan"}>
        <nav
          className="flex flex-col gap-1 min-w-[240px] p-2 font-sans text-base font-normal rounded-lg mx-4 mb-2"
          style={{
            flexBasis: "auto",
            minWidth: 0,
            width: "100%",
            background: getColor("surface"),
            color: getColor("text"),
          }}
        >
          {serdivanMenuItems.map((item, key) => (
            <Link to={item.path} key={key} onClick={() => onNavigate && onNavigate()}>
              <div
                role="button"
                tabIndex={0}
                className="flex items-center w-full p-3 rounded-lg text-start transition-all outline-none py-1 text-sm min-w-fit"
                style={{
                  color: getColor("text"),
                  background: getColor("surface"),
                }}
              >
                {item.title}
              </div>
            </Link>
          ))}
        </nav>
      </Collapse>

      {/* Kurumsal Group */}
      <ListItem
        onClick={() => handleGroupToggle("kurumsal")}
        className="flex justify-between items-center py-2 px-4 font-bold cursor-pointer min-w-fit"
        aria-expanded={openGroup === "kurumsal"}
        tabIndex={0}
        style={{
          color: getColor("navbarText"),
          background: getColor("navbar"),
        }}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") handleGroupToggle("kurumsal");
        }}
      >
        Kurumsal
        <ChevronDownIcon
          strokeWidth={2.5}
          className={`h-4 w-4 transition-transform ${openGroup === "kurumsal" ? "rotate-180" : ""}`}
          style={{ color: getColor("navbarText") }}
        />
      </ListItem>
      <Collapse open={openGroup === "kurumsal"}>
        <nav
          className="flex flex-col gap-1 min-w-[240px] p-2 font-sans text-base font-normal rounded-lg mx-4 mb-2"
          style={{
            flexBasis: "auto",
            minWidth: 0,
            width: "100%",
            background: getColor("surface"),
            color: getColor("text"),
          }}
        >
          {kurumsalMenuItems.map((item, key) => (
            <div key={key}>
              {item.submenu ? (
                <>
                  <ListItem
                    onClick={e => {
                      e.stopPropagation();
                      handleSubmenuToggle(item.title);
                    }}
                    className="flex justify-between items-center py-1 text-sm cursor-pointer min-w-fit"
                    aria-expanded={
                      (item.title === "Belediye Meclisi" && isMeclisOpen) ||
                      (item.title === "Belediye Encümeni" && isEncumenOpen) ||
                      (item.title === "Yönetim" && isYonetimOpen)
                    }
                    tabIndex={0}
                    style={{
                      color: getColor("text"),
                      background: getColor("surface"),
                    }}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") handleSubmenuToggle(item.title);
                    }}
                  >
                    {item.title}
                    <ChevronDownIcon
                      strokeWidth={2.5}
                      className={`h-3 w-3 transition-transform ${
                        (item.title === "Belediye Meclisi" && isMeclisOpen) ||
                        (item.title === "Belediye Encümeni" && isEncumenOpen) ||
                        (item.title === "Yönetim" && isYonetimOpen)
                          ? "rotate-180" : ""
                      }`}
                      style={{ color: getColor("textSecondary") }}
                    />
                  </ListItem>
                  <Collapse open={
                    (item.title === "Belediye Meclisi" && isMeclisOpen) ||
                    (item.title === "Belediye Encümeni" && isEncumenOpen) ||
                    (item.title === "Yönetim" && isYonetimOpen)
                  }>
                    <List
                      className="pl-4 rounded mx-2 mb-1"
                      style={{
                        background: getColor("card"),
                        color: getColor("cardText"),
                      }}
                    >
                      {item.submenu.map((subItem, subKey) => (
                        <Link to={subItem.path} key={subKey} onClick={() => onNavigate && onNavigate()}>
                          <ListItem
                            className="py-0.5 text-xs min-w-fit"
                            style={{
                              color: getColor("textSecondary"),
                              background: getColor("card"),
                            }}
                          >
                            • {subItem.title}
                          </ListItem>
                        </Link>
                      ))}
                    </List>
                  </Collapse>
                </>
              ) : item.external ? (
                <a href={item.path} target="_blank" rel="noopener noreferrer">
                  <ListItem
                    className="py-1 text-sm min-w-fit"
                    style={{
                      color: getColor("text"),
                      background: getColor("surface"),
                    }}
                  >
                    {item.title}
                  </ListItem>
                </a>
              ) : (
                <Link to={item.path} onClick={() => onNavigate && onNavigate()}>
                  <ListItem
                    className="py-1 text-sm min-w-fit"
                    style={{
                      color: getColor("text"),
                      background: getColor("surface"),
                    }}
                  >
                    {item.title}
                  </ListItem>
                </Link>
              )}
            </div>
          ))}
        </nav>
      </Collapse>

      <Link to="/hizmetlerimiz" onClick={() => onNavigate && onNavigate()}>
        <ListItem
          className="py-2 px-4 font-bold min-w-fit"
          style={{ color: getColor("navbarText"), background: getColor("navbar") }}
        >
          Hizmetlerimiz
        </ListItem>
      </Link>
      <Link to="/duyurular" onClick={() => onNavigate && onNavigate()}>
        <ListItem
          className="py-2 px-4 font-bold min-w-fit"
          style={{ color: getColor("navbarText"), background: getColor("navbar") }}
        >
          Duyurular
        </ListItem>
      </Link>
      <Link to="/haberler" onClick={() => onNavigate && onNavigate()}>
        <ListItem
          className="py-2 px-4 font-bold min-w-fit"
          style={{ color: getColor("navbarText"), background: getColor("navbar") }}
        >
          Haberler
        </ListItem>
      </Link>
      <Link to="/etkinlik-takvimi" onClick={() => onNavigate && onNavigate()}>
        <ListItem
          className="py-2 px-4 font-bold min-w-fit"
          style={{ color: getColor("navbarText"), background: getColor("navbar") }}
        >
          Etkinlikler
        </ListItem>
      </Link>
    </List>
  );
}

// SERDIVAN PLUS COMPONENT
function EczaneIcon({ className = "", style = {} }) {
  return (
    <div
      aria-hidden="true"
      className={"flex items-center justify-center rounded-full bg-red-600 " + className}
      style={{ width: 24, height: 24, ...style }}
    >
      <span style={{ color: "#fff", fontWeight: "bold", fontSize: "1.1rem" }}>E</span>
    </div>
  );
}

function SerdivanPlusMenu({ isMobile = false }) {
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (!e.target.closest('.serdivan-plus-container')) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  if (isMobile) {
    return (
      <div className="w-full serdivan-plus-container">
        <Button
          size="md"
          fullWidth
          style={{
            backgroundColor: "#2563eb",
            color: "#fff",
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            minWidth: "150px",
            fontSize: "1rem",
          }}
          className="shadow-md hover:scale-105 transition-transform mb-2 w-full"
          onClick={() => setIsOpen(!isOpen)}
        >
          <SquaresPlusIcon className="h-5 w-5 text-yellow-400" />
          Serdivan+
          <ChevronDownIcon
            strokeWidth={2.5}
            className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </Button>

        <Collapse open={isOpen}>
          <div className="flex flex-col gap-2">
            {/* Kültür Sanat (1. sıra) */}
            <a href="https://serdivankultursanat.org" target="_blank" rel="noopener noreferrer" className="w-full">
              <Button
                size="md"
                fullWidth
                style={{
                  backgroundColor: "#8B5CF6",
                  color: "#FFFFFF",
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
                className="shadow-md hover:scale-105 transition-transform w-full"
                onClick={() => setIsOpen(false)}
              >
                <PaintBrushIcon className="h-5 w-5 text-white" />
                Kültür Sanat
              </Button>
            </a>

            {/* SEBİK - Serdivan Belediyesi İstihdam Köprüsü (2. sıra) */}
            <a href="https://sebik.serdivan.bel.tr/" target="_blank" rel="noopener noreferrer" className="w-full">
              <Button
                size="md"
                fullWidth
                style={{
                  backgroundColor: "#eaf6f8",
                  color: "#0d5c6e",
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
                className="shadow-md hover:scale-105 transition-transform w-full"
                onClick={() => setIsOpen(false)}
              >
                <img
                  src="/sebik-logo.png"
                  alt=""
                  style={{ height: '20px', width: '20px', objectFit: 'contain' }}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
                SEBİK
              </Button>
            </a>

            {/* Nöbetçi Eczaneler */}
            <Link to="/nobetci-eczaneler">
              <Button
                size="md"
                fullWidth
                style={{
                  backgroundColor: "#dc2626", // kırmızı
                  color: "#fff",
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
                className="shadow-md hover:scale-105 transition-transform w-full"
                onClick={() => setIsOpen(false)}
              >
                <EczaneIcon />
                Nöbetçi Eczaneler
              </Button>
            </Link>
            
            {/* SABSIS - Afet Toplanma Alanları */}
            <Link to="/sabsis-serdivan-afet-bilgi-sistemi">
              <Button
                size="md"
                fullWidth
                style={{
                  backgroundColor: "#991b1b",
                  color: "#fff",
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
                className="shadow-md hover:scale-105 transition-transform w-full"
                onClick={() => setIsOpen(false)}
              >
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                SABSIS - Afet
              </Button>
            </Link>
            
            <Link to="/trafikpark">
              <Button
                size="md"
                fullWidth
                style={{
                  backgroundColor: "#f5af08",
                  color: "#000",
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
                className="shadow-md hover:scale-105 transition-transform w-full"
                onClick={() => setIsOpen(false)}
              >
                <TruckIcon className="h-5 w-5 text-blue-900" />
                TrafikPark
              </Button>
            </Link>

            <Link to="/kurek">
              <Button
                size="md"
                fullWidth
                style={{
                  backgroundColor: "#0ea5e9",
                  color: "#002",
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
                className="shadow-md hover:scale-105 transition-transform w-full"
                onClick={() => setIsOpen(false)}
              >
                <TrophyIcon className="h-5 w-5 text-yellow-500" />
                Kürek
              </Button>
            </Link>

            <Link to="/parklar">
              <Button
                size="md"
                fullWidth
                style={{
                  backgroundColor: "#059669",
                  color: "#fff",
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
                className="shadow-md hover:scale-105 transition-transform w-full"
                onClick={() => setIsOpen(false)}
              >
                <MapIcon className="h-5 w-5" />
                Parklar
              </Button>
            </Link>

            <Link to="/pazar-yerleri">
              <Button
                size="md"
                fullWidth
                style={{
                  backgroundColor: "#10B981",
                  color: "#fff",
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
                className="shadow-md hover:scale-105 transition-transform w-full"
                onClick={() => setIsOpen(false)}
              >
                <BuildingLibraryIcon className="h-5 w-5" />
                Pazar Yerleri
              </Button>
            </Link>

            <Link to="/tesislerimiz">
              <Button
                size="md"
                fullWidth
                style={{
                  backgroundColor: "#0891b2",
                  color: "#fff",
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
                className="shadow-md hover:scale-105 transition-transform w-full"
                onClick={() => setIsOpen(false)}
              >
                <BuildingOfficeIcon className="h-5 w-5" />
                Tesislerimiz
              </Button>
            </Link>

            <Link to="/atik-yonetim-rehberi">
              <Button
                size="md"
                fullWidth
                style={{
                  backgroundColor: "#7c3aed",
                  color: "#fff",
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
                className="shadow-md hover:scale-105 transition-transform w-full"
                onClick={() => setIsOpen(false)}
              >
                <SparklesIcon className="h-5 w-5" />
                Atık Yönetim
              </Button>
            </Link>

            <Link to="/planetaryum">
              <Button
                size="md"
                fullWidth
                style={{
                  backgroundColor: "#23272f",
                  color: "#f5af08",
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
                className="shadow-md hover:scale-105 transition-transform w-full"
                onClick={() => setIsOpen(false)}
              >
                <RocketLaunchIcon className="h-5 w-5 text-fuchsia-500" />
                Planetaryum
              </Button>
            </Link>

          </div>
        </Collapse>
      </div>
    );
  }

  return (
    <div className="relative serdivan-plus-container">
      <Button
        size="md"
        style={{
          backgroundColor: "#2563eb",
          color: "#fff",
          display: 'flex',
          alignItems: 'center',
          minWidth: "150px",
          fontSize: "1rem",
          fontWeight: 600,
          letterSpacing: "0.01em",
          paddingLeft: "2rem",
          paddingRight: "2rem",
          paddingTop: "0.7rem",
          paddingBottom: "0.7rem",
          borderRadius: "12px",
          gap: '0.5rem',
        }}
        className="shadow-md hover:scale-105 transition-transform"
        onClick={() => setIsOpen(!isOpen)}
      >
        <SquaresPlusIcon className="h-5 w-5 text-yellow-400" />
        Serdivan+
      </Button>

      {isOpen && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 min-w-[220px] w-max bg-white shadow-lg rounded-lg p-2 z-50"
          style={{
            boxShadow: "0 8px 32px 0 rgba(60, 60, 60, 0.12), 0 1.5px 6px 0 rgba(60,60,60,0.08)",
            borderRadius: "16px"
          }}
        >
          <div className="flex flex-col gap-2">
            {/* Kültür Sanat (1. sıra) */}
            <a href="https://serdivankultursanat.org" target="_blank" rel="noopener noreferrer">
              <Button
                size="sm"
                fullWidth
                style={{
                  backgroundColor: "#8B5CF6",
                  color: "#FFFFFF",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                className="shadow-md hover:scale-105 transition-transform"
                onClick={() => setIsOpen(false)}
              >
                <PaintBrushIcon className="h-5 w-5 text-white" />
                Kültür Sanat
              </Button>
            </a>

            {/* SEBİK - Serdivan Belediyesi İstihdam Köprüsü (2. sıra) */}
            <a href="https://sebik.serdivan.bel.tr/" target="_blank" rel="noopener noreferrer">
              <Button
                size="sm"
                fullWidth
                style={{
                  backgroundColor: "#eaf6f8",
                  color: "#0d5c6e",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                className="shadow-md hover:scale-105 transition-transform"
                onClick={() => setIsOpen(false)}
              >
                <img
                  src="/sebik-logo.png"
                  alt=""
                  style={{ height: '20px', width: '20px', objectFit: 'contain' }}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
                SEBİK
              </Button>
            </a>

            {/* Nöbetçi Eczaneler */}
            <Link to="/nobetci-eczaneler">
              <Button
                size="sm"
                fullWidth
                style={{
                  backgroundColor: "#dc2626", // kırmızı
                  color: "#fff",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                className="shadow-md hover:scale-105 transition-transform"
                onClick={() => setIsOpen(false)}
              >
                <EczaneIcon />
                Nöbetçi Eczaneler
              </Button>
            </Link>
            
            {/* SABSIS - Afet Toplanma Alanları */}
            <Link to="/sabsis-serdivan-afet-bilgi-sistemi">
              <Button
                size="sm"
                fullWidth
                style={{
                  backgroundColor: "#991b1b",
                  color: "#fff",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                className="shadow-md hover:scale-105 transition-transform"
                onClick={() => setIsOpen(false)}
              >
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                SABSIS - Afet
              </Button>
            </Link>
            
            <Link to="/trafikpark">
              <Button
                size="sm"
                fullWidth
                style={{
                  backgroundColor: "#f5af08",
                  color: "#000",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                className="shadow-md hover:scale-105 transition-transform"
                onClick={() => setIsOpen(false)}
              >
                <TruckIcon className="h-5 w-5 text-blue-900" />
                TrafikPark
              </Button>
            </Link>

            <Link to="/kurek">
              <Button
                size="sm"
                fullWidth
                style={{
                  backgroundColor: "#0ea5e9",
                  color: "#002",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                className="shadow-md hover:scale-105 transition-transform"
                onClick={() => setIsOpen(false)}
              >
                <TrophyIcon className="h-5 w-5 text-yellow-500" />
                Kürek
              </Button>
            </Link>

            <Link to="/parklar">
              <Button
                size="sm"
                fullWidth
                style={{
                  backgroundColor: "#059669",
                  color: "#fff",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                className="shadow-md hover:scale-105 transition-transform"
                onClick={() => setIsOpen(false)}
              >
                <MapIcon className="h-5 w-5" />
                Parklar
              </Button>
            </Link>

            <Link to="/pazar-yerleri">
              <Button
                size="sm"
                fullWidth
                style={{
                  backgroundColor: "#10B981",
                  color: "#fff",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                className="shadow-md hover:scale-105 transition-transform"
                onClick={() => setIsOpen(false)}
              >
                <BuildingLibraryIcon className="h-5 w-5" />
                Pazar Yerleri
              </Button>
            </Link>

            <Link to="/tesislerimiz">
              <Button
                size="sm"
                fullWidth
                style={{
                  backgroundColor: "#0891b2",
                  color: "#fff",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                className="shadow-md hover:scale-105 transition-transform"
                onClick={() => setIsOpen(false)}
              >
                <BuildingOfficeIcon className="h-5 w-5" />
                Tesislerimiz
              </Button>
            </Link>

            <Link to="/atik-yonetim-rehberi">
              <Button
                size="sm"
                fullWidth
                style={{
                  backgroundColor: "#7c3aed",
                  color: "#fff",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                className="shadow-md hover:scale-105 transition-transform"
                onClick={() => setIsOpen(false)}
              >
                <SparklesIcon className="h-5 w-5" />
                Atık Yönetim
              </Button>
            </Link>

            <Link to="/planetaryum">
              <Button
                size="sm"
                fullWidth
                style={{
                  backgroundColor: "#23272f",
                  color: "#f5af08",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                className="shadow-md hover:scale-105 transition-transform"
                onClick={() => setIsOpen(false)}
              >
                <RocketLaunchIcon className="h-5 w-5 text-fuchsia-500" />
                Planetaryum
              </Button>
            </Link>

          </div>
        </div>
      )}
    </div>
  );
}

// THEME TOGGLE BUTTON COMPONENT
function ThemeToggleButton() {
  const [theme, setThemeState] = React.useState(getCurrentTheme());

  const handleToggle = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    setThemeState(newTheme);
    // Eğer body veya root'a class ekliyorsanız burada ekleyebilirsiniz
    document.body.classList.toggle("dark", newTheme === "dark");
  };

  return (
    <Button
      variant="text"
      style={{
        color: getColor("navbarText"),
        background: "transparent",
      }}
      className="flex items-center gap-2"
      onClick={handleToggle}
      title={theme === "light" ? "Koyu Tema" : "Açık Tema"}
    >
      {theme === "light" ? (
        <MoonIcon className="h-6 w-6" style={{ color: getColor("navbarText") }} />
      ) : (
        <SunIcon className="h-6 w-6" style={{ color: getColor("primary") }} />
      )}
    </Button>
  );
}

// MAIN NAVBAR COMPONENT
export function NavbarWithMegaMenu({ goToFirstSlide }) {
   const [openNav, setOpenNav] = React.useState(false);
   const location = useLocation();

   React.useEffect(() => {
     const handleResize = () => {
       if (window.innerWidth >= 960) {
         setOpenNav(false);
       }
     };

     window.addEventListener("resize", handleResize);
     return () => window.removeEventListener("resize", handleResize);
   }, []);

   return (
    <Navbar
      className="mx-auto max-w-full px-2 py-2 sm:px-4 lg:max-w-screen-xl sticky top-0 z-40 navbar-mega rounded-xl shadow-md"
      style={{
        position: "relative",
        background: getColor("navbar"),
        color: getColor("navbarText"),
        paddingTop: "4px",    // Mobilde üst boşluğu azalt
        paddingBottom: "4px", // Mobilde alt boşluğu azalt
      }}
    >
      <div
        className="flex items-center justify-between"
        style={{
          color: getColor("navbarText"),
          paddingTop: "0px",
          paddingBottom: "0px",
          minHeight: "48px",
        }}
      >
        {/* Logo */}
        <Link
          to="/"
          className="mr-2 cursor-pointer py-1.5 sm:mr-4 lg:ml-2"
          onClick={e => {
            if (location.pathname === "/" && goToFirstSlide) {
              e.preventDefault();
              goToFirstSlide();
            }
          }}
        >
          <img
            src="/images/serdivanlogo.webp"
            alt="Serdivan Belediyesi"
            className="h-17 w-auto sm:h-12 lg:h-17"
            width="244"
            height="250"
            decoding="async"
            style={{
              maxHeight: "52px",
              minHeight: "36px",
              marginTop: "0px",
              marginBottom: "0px"
            }}
          />
        </Link>
        {/* Mobilde logo ile menü butonu arasında başlık */}
        <div
          className="block lg:hidden flex-1 text-center select-none"
          style={{
            fontWeight: "bold",
            fontSize: "1.15rem",
            color: "#946700", /* beyaz navbar üzerinde WCAG AA (~5:1); eski #f5af08 ~1.9:1 idi */
            letterSpacing: "0.04em",
            lineHeight: "1",
            paddingLeft: "4px",
            paddingRight: "4px"
          }}
        >
          Serdivan Belediyesi
        </div>
        <div className="hidden lg:flex flex-1 justify-center">
          <DesktopMenu goToFirstSlide={goToFirstSlide} location={location} />
        </div>
        {/* Serdivan+ butonu ve tema toggle için alan */}
        <div className="hidden lg:flex gap-2 items-center">
          <SerdivanPlusMenu />
          {/* <ThemeToggleButton /> */}
        </div>
        <IconButton
          variant="text"
          style={{ color: getColor("navbarText") }}
          className="lg:hidden"
          onClick={() => setOpenNav(!openNav)}
          aria-label={openNav ? "Menüyü kapat" : "Menüyü aç"}
          aria-expanded={openNav}
        >
          {openNav ? (
            <XMarkIcon className="h-6 w-6" strokeWidth={2} style={{ color: getColor("navbarText") }} />
          ) : (
            <Bars3Icon className="h-6 w-6" strokeWidth={2} style={{ color: getColor("navbarText") }} />
          )}
        </IconButton>
      </div>

      {/* Mobile Navigation - Sadece mobile ve tablet */}
      <Collapse open={openNav} className="lg:hidden">
        <div className="px-2 pb-4" style={{ background: getColor("surface"), color: getColor("text") }}>
          <MobileMenu
            goToFirstSlide={goToFirstSlide}
            location={location}
            onNavigate={() => setOpenNav(false)}
          />
          <div className="mt-4">
            <SerdivanPlusMenu isMobile={true} />
          </div>
          <div className="mt-2 flex justify-end">
            {/* <ThemeToggleButton /> */}
          </div>
        </div>
      </Collapse>
    </Navbar>
   );
 }
 
 export default NavbarWithMegaMenu;
